import React, { FC, memo } from 'react';
import { Text, View } from '@react-pdf/renderer';

import { BASE_FONT_SIZE } from '../pdfStyles';
import { Phone } from '../../../api/schemas/phoneSchema';

export type BillingContactPDFType = {
    name: string;
    address: string;
    city: string;
    country: string;
    postalCode: string;
    vatNumber: string;
    phones: Phone[];
};

type Props = {
    name: string;
    address: string;
    city: string;
    country: string;
    postalCode: string;
    vatNumber: string;
    phones: Phone[];
    withTo?: boolean;
};

type Address = {
    address_text?: string;
    city_name?: string;
    country_name?: string;
    zip_code?: string;
};

const PdfBillingContactDetails: FC<Props> = (props: Props) => {
    const { name, address, city, country, postalCode, phones, vatNumber, withTo } = props;

    const formattedAddress: Address =
        typeof address === 'object' && address !== null ? address : {};

    const displayCity = city || formattedAddress.city_name || '';
    const displayCountry = country || formattedAddress.country_name || '';
    const displayPostalCode = postalCode || formattedAddress.zip_code || '';

    // Build address lines — trim all whitespace to prevent alignment issues
    const addressLines: string[] = [];
    if (formattedAddress.address_text) {
        formattedAddress.address_text.split(/\r?\n/).forEach((line) => {
            const trimmed = line.trim();
            if (trimmed) addressLines.push(trimmed);
        });
    } else if (typeof address === 'string' && address.trim()) {
        addressLines.push(address.trim());
    }
    if (displayCity?.trim()) addressLines.push(displayCity.trim());
    if (displayPostalCode?.trim()) addressLines.push(displayPostalCode.trim());
    if (displayCountry?.trim()) addressLines.push(displayCountry.trim());

    // Dedupe phones
    const seen = new Set<string>();
    const displayPhones = (phones || []).filter((phone) => {
        if (!phone?.number) return false;
        const key = `${phone.type}|${phone.number}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    return (
        <View style={{ marginBottom: 4 }}>
            {/* "To" label — subtle */}
            {withTo ? (
                <Text style={{ fontSize: BASE_FONT_SIZE - 1, color: '#888', marginBottom: 2 }}>To:</Text>
            ) : null}

            {/* Name — prominent */}
            <Text style={{ fontWeight: 'bold', fontSize: BASE_FONT_SIZE + 1, marginBottom: 2 }}>
                {(name || '').trim()}
            </Text>

            {/* Address lines — secondary */}
            {addressLines.length > 0 && (
                <View style={{ marginBottom: 2 }}>
                    {addressLines.map((line, idx) => (
                        <Text
                            key={idx}
                            style={{ fontSize: BASE_FONT_SIZE, color: '#555' }}
                        >
                            {line}
                        </Text>
                    ))}
                </View>
            )}

            {/* Phones — clean label:value rows */}
            {displayPhones.length > 0 && (
                <View style={{ marginTop: 2 }}>
                    {displayPhones.map((phone, idx) => (
                        <View key={idx} style={{ flexDirection: 'row', paddingVertical: 1 }}>
                            <Text style={{ width: 45, fontSize: BASE_FONT_SIZE, color: '#777' }}>
                                {(phone.type || '').replace('Main ', '').replace(' Phone', '')}:
                            </Text>
                            <Text style={{ fontSize: BASE_FONT_SIZE }}>{phone.number}</Text>
                        </View>
                    ))}
                </View>
            )}

            {/* VAT — smallest detail */}
            {vatNumber ? (
                <Text style={{ fontSize: BASE_FONT_SIZE - 1, color: '#777', marginTop: 2 }}>
                    VAT Number: {vatNumber}
                </Text>
            ) : null}
        </View>
    );
};

export default memo(PdfBillingContactDetails);
